import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookmarkService, isRibbonBookmark, isSavedBookmark } from '../../services/bookmarks';
import { bibleService } from '../../services/bible';
import { t } from '../../i18n/uiText';
import AddBookmarkDialog from './AddBookmarkDialog';
import BfSheet from './bf/BfSheet';

const SNIPPET_MAX = 160;
const UNDO_MS = 5000;
const TAB_KEY = 'bf.savedTab';
const TAB_SAVED = 'saved';
const TAB_RIBBONS = 'ribbons';

function storedTab() {
  try {
    const value = localStorage.getItem(TAB_KEY);
    return value === TAB_SAVED || value === TAB_RIBBONS ? value : null;
  } catch {
    return null;
  }
}

function snippetOf(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length <= SNIPPET_MAX ? clean : `${clean.slice(0, SNIPPET_MAX - 1).trimEnd()}…`;
}

/**
 * «Сохранённое»: две вкладки — стихи и главы (из приложения и из Telegram) и ленточки «где я читаю».
 * Удаление — сразу, с возможностью вернуть.
 */
export default function SavedSheet({
  translation = 'synodal',
  bookId,
  chapter,
  bookName,
  onClose,
  onChanged,
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState(storedTab);
  const [books, setBooks] = useState([]);
  const [snippets, setSnippets] = useState({});
  const [removed, setRemoved] = useState(null);
  const [showAddRibbon, setShowAddRibbon] = useState(false);
  const [busy, setBusy] = useState(false);
  const undoTimer = useRef(null);
  const authed = Boolean(localStorage.getItem('authToken'));
  const currentBook = bookId ? parseInt(bookId, 10) : null;
  const currentChapter = chapter ? parseInt(chapter, 10) : null;

  const load = async () => {
    try {
      setItems((await bookmarkService.getBookmarks()) || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    bibleService.getBooks(translation).then((list) => setBooks(list || [])).catch(() => {});
    return () => clearTimeout(undoTimer.current);
  }, [translation]);

  const ribbons = useMemo(
    () => (items || []).filter(isRibbonBookmark).sort((a, b) => a.id - b.id),
    [items]
  );
  const saved = useMemo(
    () =>
      (items || [])
        .filter(isSavedBookmark)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id),
    [items]
  );

  // Текст сохранённых стихов — чтобы в списке было видно, что именно сохранено.
  useEffect(() => {
    let cancelled = false;
    const missing = saved.filter((b) => b.verse && b.chapter && snippets[b.id] === undefined).slice(0, 40);
    if (missing.length === 0) return undefined;
    (async () => {
      const found = {};
      for (const b of missing) {
        try {
          const data = await bibleService.getChapter(b.bookId, b.chapter, translation);
          const row = data?.verses?.find((v) => parseInt(v.verse, 10) === b.verse);
          found[b.id] = row ? snippetOf(row.text) : '';
        } catch {
          found[b.id] = '';
        }
      }
      if (!cancelled) setSnippets((prev) => ({ ...prev, ...found }));
    })();
    return () => {
      cancelled = true;
    };
  }, [saved, translation]);

  const placeLabel = (b) => {
    const book = books.find((item) => item.id === b.bookId);
    const name = book?.name || `${t('bookWord')} ${b.bookId}`;
    if (!b.chapter) return name;
    return b.verse ? `${name} ${b.chapter}:${b.verse}` : `${name} ${b.chapter}`;
  };

  const isHere = (b) => b.bookId === currentBook && b.chapter === currentChapter;

  const chapterSaved = saved.some((b) => isHere(b) && !b.verse);

  const open = (b) => {
    let url = `/${translation}/${b.bookId}`;
    if (b.chapter) {
      url += `/${b.chapter}`;
      if (b.verse) url += `/${b.verse}`;
    }
    navigate(url);
    onClose?.();
  };

  const changed = async () => {
    await load();
    onChanged?.();
  };

  const moveHere = async (b, e) => {
    e.stopPropagation();
    if (busy || !currentBook || !currentChapter) return;
    setBusy(true);
    try {
      await bookmarkService.updateBookmark(b.id, currentBook, currentChapter, null, b.note || null);
      await changed();
    } catch {
      alert(t('bookmarkMoveError'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b, e) => {
    e.stopPropagation();
    setItems((prev) => (prev || []).filter((item) => item.id !== b.id));
    try {
      await bookmarkService.deleteBookmark(b.id);
      clearTimeout(undoTimer.current);
      setRemoved(b);
      undoTimer.current = setTimeout(() => setRemoved(null), UNDO_MS);
      onChanged?.();
    } catch {
      alert(t('bookmarkDeleteError'));
      await load();
    }
  };

  const restore = async () => {
    const b = removed;
    clearTimeout(undoTimer.current);
    setRemoved(null);
    if (!b) return;
    try {
      await bookmarkService.addBookmark(b.bookId, b.chapter, b.verse, b.note || null, b.kind);
      await changed();
    } catch {
      await load();
    }
  };

  const saveChapter = async () => {
    if (busy || !currentBook || !currentChapter || chapterSaved) return;
    setBusy(true);
    try {
      await bookmarkService.saveVerse(currentBook, currentChapter, null);
      await changed();
    } catch {
      alert(t('bookmarkAddError'));
    } finally {
      setBusy(false);
    }
  };

  const deleteButton = (b) => (
    <button
      type="button"
      onClick={(e) => remove(b, e)}
      className="bf-bm-del"
      title={t('bookmarkDeleteTitle')}
      aria-label={t('bookmarkDeleteTitle')}
    >
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  );

  const inChapter = Boolean(currentBook && currentChapter);
  const activeTab = tab ?? (saved.length === 0 && ribbons.length > 0 ? TAB_RIBBONS : TAB_SAVED);
  const pickTab = (next) => {
    setTab(next);
    try {
      localStorage.setItem(TAB_KEY, next);
    } catch {
      /* private mode */
    }
  };

  const canAct = authed && inChapter;
  const footer =
    removed || canAct ? (
      <div className="bf-saved-footer">
        {removed && (
          <div className="bf-saved-undo" role="status">
            <span>{t('savedRemoved')}</span>
            <button type="button" onClick={restore}>
              {t('undoAction')}
            </button>
          </div>
        )}
        {canAct && activeTab === TAB_SAVED && (
          <button
            type="button"
            className="bf-bm-add bf-saved-footer__btn"
            onClick={saveChapter}
            disabled={busy || chapterSaved}
          >
            <svg className="bf-bm-add__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" />
            </svg>
            <span>{chapterSaved ? t('chapterSaved') : t('saveChapter')}</span>
          </button>
        )}
        {canAct && activeTab === TAB_RIBBONS && (
          <button
            type="button"
            className="bf-bm-add bf-saved-footer__btn"
            onClick={() => setShowAddRibbon(true)}
          >
            <svg className="bf-bm-add__icon bf-bm-add__icon--stroke" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>{t('newRibbon')}</span>
          </button>
        )}
      </div>
    ) : null;

  const tabButton = (id, label, count) => (
    <button
      type="button"
      role="tab"
      id={`bf-saved-tab-${id}`}
      aria-selected={activeTab === id}
      aria-controls={`bf-saved-panel-${id}`}
      className="bf-rs-tab bf-saved-tab"
      data-active={activeTab === id ? 'true' : 'false'}
      onClick={() => pickTab(id)}
    >
      <span>{label}</span>
      {count > 0 && <span className="bf-saved-tab__count">{count}</span>}
    </button>
  );

  const toolbar = (
    <div className="bf-rs-tabs bf-saved-tabs" role="tablist" aria-label={t('savedTitle')}>
      {tabButton(TAB_SAVED, t('savedItemsTitle'), saved.length)}
      {tabButton(TAB_RIBBONS, t('ribbonsTitle'), ribbons.length)}
    </div>
  );

  return (
    <>
      <BfSheet
        title={t('savedTitle')}
        titleMark
        onClose={onClose}
        ariaLabel={t('savedTitle')}
        sheetClassName="bf-sheet--fit-content bf-sheet--saved"
        toolbar={toolbar}
        footer={footer}
      >
        {items === null ? (
          <div className="bf-bm-loading">
            <div className="bf-bm-loading__spinner" aria-hidden="true" />
            <p>{t('bookmarksLoading')}</p>
          </div>
        ) : activeTab === TAB_SAVED ? (
          <div className="bf-saved" role="tabpanel" id={`bf-saved-panel-${TAB_SAVED}`} aria-labelledby={`bf-saved-tab-${TAB_SAVED}`}>
            {saved.length === 0 ? (
              <div className="bf-bm-empty bf-saved-empty">{t('savedEmpty')}</div>
            ) : (
              <div className="bf-bm-list">
                {saved.map((b) => (
                  <div
                    key={b.id}
                    className="bf-bm-item bf-saved-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => open(b)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') open(b);
                    }}
                  >
                    <div className="bf-bm-item__body">
                      <p className="bf-bm-item__title">{placeLabel(b)}</p>
                      {b.verse && snippets[b.id] ? (
                        <p className="bf-saved-item__text">{snippets[b.id]}</p>
                      ) : null}
                      {b.note ? (
                        <p className="bf-bm-item__sub">
                          <span className="bf-bm-item__sub-mark" aria-hidden="true" />
                          {b.note}
                        </p>
                      ) : null}
                    </div>
                    <div className="bf-bm-item__actions">{deleteButton(b)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bf-saved" role="tabpanel" id={`bf-saved-panel-${TAB_RIBBONS}`} aria-labelledby={`bf-saved-tab-${TAB_RIBBONS}`}>
            {ribbons.length === 0 ? (
              <div className="bf-bm-empty bf-saved-empty">{t('ribbonsEmpty')}</div>
            ) : (
              <div className="bf-bm-list">
                {ribbons.map((b) => (
                  <div
                    key={b.id}
                    className={`bf-bm-item bf-saved-item ${isHere(b) ? 'bf-bm-item--current' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => open(b)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') open(b);
                    }}
                  >
                    <div className="bf-bm-item__body">
                      <p className="bf-bm-item__title">{b.note || t('bookmarkUntitled')}</p>
                      <p className="bf-bm-item__sub">
                        <span className="bf-bm-item__sub-mark" aria-hidden="true" />
                        {placeLabel(b)}
                      </p>
                    </div>
                    <div className="bf-bm-item__actions">
                      {inChapter && !isHere(b) && (
                        <button
                          type="button"
                          className="bf-bm-go"
                          onClick={(e) => moveHere(b, e)}
                          disabled={busy}
                          title={t('ribbonMoveHere')}
                        >
                          {t('ribbonMoveHere')}
                        </button>
                      )}
                      {deleteButton(b)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </BfSheet>

      {showAddRibbon && (
        <AddBookmarkDialog
          bookId={bookId}
          chapter={chapter}
          verse={null}
          bookName={bookName}
          onClose={() => setShowAddRibbon(false)}
          onBookmarkAdded={changed}
        />
      )}
    </>
  );
}
